import React, { useEffect, useState, useMemo } from "react";
import SummaryApi from "../common";
import { toast } from "react-toastify";
import { MdEdit, MdClose, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { X, Star, MessageSquare, Send } from "lucide-react";
const CustomerReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterDate, setFilterDate] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.getAllAdminReviews.url, {
        method: SummaryApi.getAllAdminReviews.method,
      });
      const result = await response.json();
      if (result.status) {
        setReviews(result.data);
      }
    } catch (err) {
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterDate]);

  // --- Filtering Logic ---
 // --- Filtering Logic ---
const filteredData = useMemo(() => {
  return reviews.filter((review) => {
    const matchesSearch =
      review.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.review_description
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    // FIX START: Convert DB date to local YYYY-MM-DD format for accurate filtering
    let matchesDate = true;
    if (filterDate) {
      const localReviewDate = new Date(review.review_date).toLocaleDateString('en-CA'); // 'en-CA' returns YYYY-MM-DD
      matchesDate = localReviewDate === filterDate;
    }
    // FIX END

    let category = "Sakhi Order";
    if (review.product_title) category = "Product";
    else if (review.user_pack_subscription_id !== 0)
      category = "Subscription";

    const matchesCategory =
      filterCategory === "All" || category === filterCategory;

    return matchesSearch && matchesDate && matchesCategory;
  });
}, [reviews, searchTerm, filterCategory, filterDate]);

  // --- Pagination Logic ---
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  // --- Update Logic ---
  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    try {
      const payload = {
        review_id: editingReview.review_id,
        review_description: editingReview.review_description,
        is_active: editingReview.is_active,
        product_id: editingReview.product_id || 0,
        order_id: editingReview.order_id || 0,
        user_pack_subscription_id: editingReview.user_pack_subscription_id || 0,
      };

      const response = await fetch(SummaryApi.review_update.url, {
        method: SummaryApi.review_update.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.status) {
        toast.success("Review updated successfully!");
        setShowModal(false);
        fetchReviews();
      }
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading)
    return (
      <div className="p-10 text-center font-medium text-gray-500">
        Loading Dashboard...
      </div>
    );

  return (
    <div className="p-8 bg-[#F9FAFB] min-h-screen font-sans">
      <h2 className="text-2xl font-bold text-[#1F2937] mb-8">
        Customer Reviews
      </h2>

      {/* --- Filter Bar --- */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Search name or comment..."
          className="flex-1 min-w-[250px] p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-200 outline-none transition-all"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="p-2.5 border border-gray-200 rounded-lg text-sm bg-white outline-none min-w-[160px]"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="All">All Categories</option>
          <option value="Product">Product</option>
          <option value="Subscription">Subscription</option>
          <option value="Sakhi Order">Sakhi Order</option>
        </select>
        <input
          type="date"
          className="p-2.5 border border-gray-200 rounded-lg text-sm outline-none text-gray-500"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
        <button
          onClick={() => {
            setSearchTerm("");
            setFilterCategory("All");
            setFilterDate("");
          }}
          className="text-sm font-semibold text-blue-600 hover:underline px-4"
        >
          Reset All Filters
        </button>
      </div>

      {/* --- Table Content --- */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#FDF2F8] text-[#6B7280] text-xs uppercase tracking-wider font-bold">
              <th className="p-5 border-b border-gray-100">S.no</th>
              <th className="p-5 border-b border-gray-100">Customer</th>
              <th className="p-5 border-b border-gray-100">Category</th>
              <th className="p-5 border-b border-gray-100">Comment</th>
              <th className="p-5 border-b border-gray-100">Date</th>
              <th className="p-5 border-b border-gray-100 text-center">
                Status
              </th>
              <th className="p-5 border-b border-gray-100 text-center">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="text-sm text-gray-700 divide-y divide-gray-50">
            {currentRecords.map((review, index) => (
              <tr
                key={review.review_id}
                className="hover:bg-pink-50/30 transition-colors"
              >
                <td className="p-5 text-gray-400">
                  {indexOfFirstRecord + index + 1}
                </td>
                <td className="p-5 font-semibold text-gray-800">
                  {review.user_name}
                </td>
                <td className="p-5">
                  <span
                    className={`px-3 py-1 rounded-md text-[11px] font-bold ${
                      review.product_title
                        ? "bg-blue-50 text-blue-600"
                        : review.user_pack_subscription_id !== 0
                          ? "bg-purple-50 text-purple-600"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {review.product_title ||
                      (review.user_pack_subscription_id !== 0
                        ? "Subscription"
                        : "Sakhi Order")}
                  </span>
                </td>
               <td className="p-5 italic text-gray-500 max-w-[300px] whitespace-normal break-words leading-relaxed">
  "{review.review_description}"
</td>
                <td className="p-5">
                  {new Date(review.review_date).toLocaleDateString()}
                </td>
                <td className="p-5 text-center">
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-black tracking-tighter uppercase ${review.is_active ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}
                  >
                    {review.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-5 text-center">
                  <button
                    onClick={() => {
                      setEditingReview({ ...review });
                      setShowModal(true);
                    }}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                  >
                    <MdEdit size={20} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* --- Pagination Footer --- */}
        <div className="p-5 border-t border-gray-50 flex items-center justify-between bg-white">
          <p className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-semibold text-gray-800">
              {indexOfFirstRecord + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold text-gray-800">
              {Math.min(indexOfLastRecord, filteredData.length)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-800">
              {filteredData.length}
            </span>{" "}
            results
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-all"
            >
              <MdChevronLeft size={20} />
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all ${currentPage === i + 1 ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "text-gray-600 hover:bg-gray-50"}`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-all"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* --- Edit Modal --- */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-6 border-b border-gray-50">
              <h3 className="text-lg font-bold text-gray-800">Edit Review</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <MdClose size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6">
              <div className="mb-5">
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                  Customer Name
                </label>
                <input
                  type="text"
                  disabled
                  value={editingReview.user_name}
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-gray-500"
                />
              </div>

              <div className="mb-5">
                 <div className="flex justify-between items-center">
                    <p className="text-xs font-black text-rose-900/40 uppercase flex items-center gap-2">
                      <MessageSquare size={14} /> Review Description
                    </p>
                    {/* Character Counter */}
                    <span
                      className={`text-[10px] font-bold ${editingReview.review_description.length >= 300 ? "text-red-500" : "text-rose-400"}`}
                    >
                      {editingReview.review_description.length}/300
                    </span>
                  </div>
                <textarea
                  className="w-full p-4 border border-gray-200 rounded-xl h-32 focus:ring-4 focus:ring-blue-50 outline-none resize-none transition-all text-sm"
                  value={editingReview.review_description}
                  maxLength={300}
                  onChange={(e) =>
                    setEditingReview({
                      ...editingReview,
                      review_description: e.target.value,
                    })
                  }
                  required
                />
                 {editingReview.review_description.length >= 300 && (
                    <p className="text-[10px] text-red-500 font-medium">
                      Character limit reached.
                    </p>
                  )}
              </div>

              <div className="mb-8 flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <span className="text-sm font-bold text-gray-700">
                  Display Status
                </span>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-[10px] font-black uppercase ${editingReview.is_active ? "text-green-600" : "text-gray-400"}`}
                  >
                    {editingReview.is_active ? "Active" : "Hidden"}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={editingReview.is_active === 1}
                      onChange={(e) =>
                        setEditingReview({
                          ...editingReview,
                          is_active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <div className="w-12 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 font-bold text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateLoading}
                  className="flex-1 py-3 font-bold bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all"
                >
                  {updateLoading ? "Saving..." : "Update Review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerReviews;
